/**
 * 74.20 (DECISIONS 439 (4): "past the patch, beds and broad beds as strips on the mesh, as rendering/landformFar.ts"): the
 * drainage's beds drawn at any distance. The fine patch tints the person's ground (rendering/fineTerrainPatch.ts, the beds'
 * tint in each cell) and fades back to the planet's mesh between 29 and 53 people; past that a bed would vanish. Here each
 * bed and broad bed (a gully is too fine to read far off) is a ribbon over its line: a point every `bodySteps` of the line's,
 * the bed's width across, on the mesh's own ground (`getSurfaceRadius`) a hair over it and drawn ahead of it in depth
 * (polygon offset), in the mesh's colour under it tinted to the bed's floor (riverbedLook.ts's rule, at the bed's middle).
 * Lines through a lake basin are drawn too (439 (5); the water itself deferred). A child of the planet's mesh, lifted by the
 * mesh's own far lift and sink (the caller chains the mesh's material hooks). Built once with the mesh. Presentation only.
 */
import * as THREE from 'three';
import type { PlanetState } from '../types';
import type { DrainageLines, DrainageHit } from '../core/drainageLines';
import { BED_KINDS, DRAINAGE_CLASSES } from '../core/drainageLines';
import { getSurfaceRadius, nearestSurfaceVertex } from '../core/surface';
import { bedTint, applyBedTint, type BedTint } from './riverbedLook';
import { applyTopoOverlay } from './topoOverlay';
import { applyGroundLift } from './groundLift';
import { applyHazeLift } from './hazeLift';

/** The strips' dials: a ribbon point every this many of the line's (3 people apart); how far over the mesh (people). */
export const RIVERBED_FAR = { bodySteps: 2, overPeople: 0.08 };

export interface RiverbedStrips { positions: Float32Array; colors: Float32Array; index: Uint32Array; triangles: number }

/** The beds' ribbons (positions relative to the planet's centre), or null where the lines have no bed. */
export function riverbedStrips(planet: PlanetState, dl: DrainageLines, person: number, meshColors?: Float32Array): RiverbedStrips | null {
  const R = planet.radius, k = person / R, over = RIVERBED_FAR.overPeople * person, S = RIVERBED_FAR.bodySteps;
  const pos: number[] = [], col: number[] = [], idx: number[] = [];
  const tint: BedTint = { mul: 1, w: 0, r: 0, g: 0, b: 0 }, c = new Float32Array(3);
  const hit = { line: 0, point: 0, across: 0, edge: 0, half: 0, bank: 0, cls: 'bed', kind: 'damp', reed: false, lake: false } as DrainageHit;
  dl.lines.forEach(ln => {
    const m = ln.width.length;
    let prev = -1;   // the last ribbon pair's first vertex, while the run of beds holds
    for (let i = 0; i < m; i++) {
      if (ln.cls[i] === 0) { prev = -1; continue; }   // (a gully: the run of beds ends)
      const runEnd = i === m - 1 || ln.cls[i + 1] === 0;
      if (prev >= 0 && !runEnd && i % S !== 0) continue;   // (a run's first and last points, and every S-th between)
      const px = ln.dirs[3 * i], py = ln.dirs[3 * i + 1], pz = ln.dirs[3 * i + 2];
      const a = Math.max(0, i - 1), b = Math.min(m - 1, i + 1);
      const tx = ln.dirs[3 * b] - ln.dirs[3 * a], ty = ln.dirs[3 * b + 1] - ln.dirs[3 * a + 1], tz = ln.dirs[3 * b + 2] - ln.dirs[3 * a + 2];
      let sx = ty * pz - tz * py, sy = tz * px - tx * pz, sz = tx * py - ty * px;
      const sl = Math.sqrt(sx * sx + sy * sy + sz * sz) || 1; sx /= sl; sy /= sl; sz /= sl;
      const half = (ln.width[i] / 2) * k;
      // the colour: the mesh's under the line, toward the bed's floor (a broad bed's channel left to the patch)
      const v = nearestSurfaceVertex(planet, { x: px, y: py, z: pz });
      if (meshColors) { c[0] = meshColors[3 * v]; c[1] = meshColors[3 * v + 1]; c[2] = meshColors[3 * v + 2]; } else { c[0] = 0.36; c[1] = 0.54; c[2] = 0.42; }
      hit.cls = DRAINAGE_CLASSES[ln.cls[i]]; hit.kind = BED_KINDS[ln.kind[i]]; hit.half = ln.width[i] / 2; hit.edge = -hit.half; hit.across = hit.half;
      applyBedTint(c, 0, bedTint(hit, 0.5, tint));
      const base = pos.length / 3;
      for (const sg of [-1, 1]) {
        let dx = px + sx * half * sg, dy = py + sy * half * sg, dz = pz + sz * half * sg;
        const l = Math.sqrt(dx * dx + dy * dy + dz * dz); dx /= l; dy /= l; dz /= l;
        const r = getSurfaceRadius(planet, { x: dx, y: dy, z: dz }) + over;
        pos.push(dx * r, dy * r, dz * r); col.push(c[0], c[1], c[2]);
      }
      if (prev >= 0) idx.push(prev, prev + 1, base, prev + 1, base + 1, base);
      prev = base;
    }
  });
  if (!idx.length) return null;
  return { positions: Float32Array.from(pos), colors: Float32Array.from(col), index: Uint32Array.from(idx), triangles: idx.length / 3 };
}

/** The strips as a mesh for the planet's mesh to hold (its origin the planet's centre): the material's lift is the caller's. */
export function riverbedFarMesh(planet: PlanetState, dl: DrainageLines, person: number, meshColors?: Float32Array): THREE.Mesh | null {
  const s = riverbedStrips(planet, dl, person, meshColors);
  if (!s) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(s.positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(s.colors, 3));
  geo.setIndex(new THREE.BufferAttribute(s.index, 1));
  geo.computeVertexNormals();
  const mesh = new THREE.Mesh(geo, riverbedFarMaterial());
  mesh.name = 'riverbedFar';
  return mesh;
}

/** The strips' material: the mesh's look (flat Lambert, vertex colours), drawn ahead of the mesh it lies on. */
export function riverbedFarMaterial(): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 });
}

/**
 * The strips on a planet's mesh (rendering/renderer.ts createPlanetMesh's): coloured from the mesh's own vertex colours, its
 * material chained as the mesh's far records' are (the topo overlay, the far lift and sink, the haze lift), added as its
 * child. Returns the strips' mesh, or null where the lines have no bed.
 */
export function attachRiverbedFar(planetMesh: THREE.Mesh, planet: PlanetState, dl: DrainageLines, person: number): THREE.Mesh | null {
  const colours = planetMesh.geometry.getAttribute('color')?.array as Float32Array | undefined;
  const far = riverbedFarMesh(planet, dl, person, colours);
  if (!far) return null;
  const m = far.material as THREE.Material;
  applyTopoOverlay(m); applyGroundLift(m); applyHazeLift(m);
  planetMesh.add(far);
  return far;
}
